import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { captureException } from "@sentry/react";
import { isHttpUrl } from "@/utils/link";
import { z } from "zod";

// Override only response transforms: keep auth/retry interceptors, preserve the
// public result schema verbatim, and camelCase only the API envelope below.
export const v2ResponseConfig = {
  transformResponse: axios.defaults.transformResponse,
};

/** lakers enrichment; absent on older responses and separate from registry text. */
export const corporateNumberMatchSchema = z.object({
  法人番号: z
    .string()
    .regex(/^\d{13}$/)
    .nullable(),
  会社法人等番号: z
    .string()
    .regex(/^\d{12}$/)
    .nullable(),
  決め方: z.enum(["registry", "master_match"]).nullable(),
});
export const v2PartyNumbersSchema = z.object({
  法人番号の突合: corporateNumberMatchSchema.nullish(),
});

const v2StatusSchema = z.object({
  id: z.number(),
  v2_status: z.enum(["succeeded", "failed"]).nullable(),
  v2_error_code: z.string().nullable(),
  v2_analyzed_at: z.string().nullable(),
  has_result: z.boolean(),
});

interface V2DetailStatus {
  id: number;
  v2Status: "succeeded" | "failed" | null;
  v2ErrorCode: string | null;
  v2AnalyzedAt: string | null;
  hasResult: boolean;
}

const statusToClient = (
  value: z.infer<typeof v2StatusSchema>
): V2DetailStatus => ({
  id: value.id,
  v2Status: value.v2_status,
  v2ErrorCode: value.v2_error_code,
  v2AnalyzedAt: value.v2_analyzed_at,
  hasResult: value.has_result,
});

export const v2ListSchema = z
  .object({
    request_id: z.number(),
    details: z.array(v2StatusSchema),
  })
  .transform((value) => ({
    requestId: value.request_id,
    details: value.details.map(statusToClient),
  }));

// The PDF comes from its own endpoint (v2PdfSchema) so it never waits for the result.
export const v2ResultSchema = v2StatusSchema
  .extend({
    result_analyzed_at: z.string().nullable(),
    result: z.record(z.unknown()).nullable(),
  })
  .transform((value) => ({
    ...statusToClient(value),
    resultAnalyzedAt: value.result_analyzed_at,
    result: value.result,
  }));

export type V2DetailResult = z.infer<typeof v2ResultSchema>;

export const v2PdfSchema = z
  .object({ pdf_url: z.string().nullable() })
  .transform((value) => ({ pdfUrl: value.pdf_url }));

export type V2DetailPdf = z.infer<typeof v2PdfSchema>;

const amzDate = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

/**
 * Expiry (epoch ms) of an S3 presigned URL, from SigV4 `X-Amz-Date` +
 * `X-Amz-Expires` or SigV2 `Expires`. Null when the URL carries neither.
 */
export const signedUrlExpiresAt = (url: string): number | null => {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return null;
  }
  const date = amzDate.exec(params.get("X-Amz-Date") ?? "");
  const seconds = Number(params.get("X-Amz-Expires"));
  if (date && Number.isFinite(seconds)) {
    const [, y, mo, d, h, mi, s] = date.map(Number);
    return Date.UTC(y, mo - 1, d, h, mi, s) + seconds * 1000;
  }
  const expires = Number(params.get("Expires"));
  return params.has("Expires") && Number.isFinite(expires)
    ? expires * 1000
    : null;
};

/** True once a signed URL is within `marginMs` of expiring. Unsigned URLs never expire. */
export const isSignedUrlExpired = (
  url: string,
  now = Date.now(),
  marginMs = 60_000
): boolean => {
  const expiresAt = signedUrlExpiresAt(url);
  return expiresAt != null && expiresAt - marginMs <= now;
};
export type V2DetailList = z.infer<typeof v2ListSchema>;

export const v2DetailsUrl = (requestId: number): string =>
  `/all-matters-analyze/${requestId}/v2/details`;

export const v2DetailUrl = (requestId: number, detailId: number): string =>
  `${v2DetailsUrl(requestId)}/${detailId}`;

export const v2PdfUrl = (detailUrl: string): string => `${detailUrl}/pdf`;

export const v2ExcelSchema = z.object({
  status: z.enum([
    "not_requested",
    "queued",
    "running",
    "succeeded",
    "failed",
    "expired",
    "stale",
    "visibility_restricted",
  ]),
  url: z.string().url().nullable(),
  error_code: z
    .enum([
      "TIMEOUT",
      "ACCESS_CHANGED",
      "SOURCE_CHANGED",
      "EXCEL_LIMIT",
      "EXCEL_DETAIL_LIMIT",
      "EXCEL_TIME_LIMIT",
      "EXCEL_FILE_LIMIT",
      "GENERATION_FAILED",
    ])
    .nullable(),
  expires_in: z.number().nullable(),
});
export type V2ExcelExport = z.infer<typeof v2ExcelSchema>;

export const v2DownloadUrl = (requestId: number): string =>
  `/all-matters-analyze/${requestId}/v2/download-file`;

/** HTTP boundaries keep the public result untouched; callers own SWR and cancellation. */
export const fetchV2Details = async (
  client: AxiosInstance,
  url: string
): Promise<V2DetailList> =>
  v2ListSchema.parse((await client.get<unknown>(url, v2ResponseConfig)).data);

export const fetchV2Detail = async (
  client: AxiosInstance,
  url: string
): Promise<V2DetailResult> =>
  v2ResultSchema.parse((await client.get<unknown>(url, v2ResponseConfig)).data);

export const fetchV2Pdf = async (
  client: AxiosInstance,
  url: string
): Promise<V2DetailPdf> =>
  v2PdfSchema.parse((await client.get<unknown>(url, v2ResponseConfig)).data);

class V2ExcelContractError extends Error {}

const reportExcelError = (cause: unknown, signal: AbortSignal): void => {
  if (signal.aborted || axios.isCancel(cause)) return;
  const status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
  if (
    (status != null && status >= 500) ||
    cause instanceof V2ExcelContractError
  ) {
    // Never pass the original Axios/Zod exception: it can contain signed URLs,
    // response bodies and personal data. Only fixed categories and status are safe.
    captureException(new Error("V2 Excel request failed"), {
      tags: {
        feature: "allmatters-v2-excel",
        category: cause instanceof V2ExcelContractError ? "contract" : "server",
        ...(status != null && { status: String(status) }),
      },
    });
  }
};

/**
 * The property-detail file is a separate job; omitting the query keeps the plain file.
 * Its status check carries no URL: only `download=true` issues one, and the first issue is billed.
 */
const v2ExcelConfig = (
  signal: AbortSignal,
  includePropertyDetail: boolean,
  download = false
): AxiosRequestConfig => ({
  ...v2ResponseConfig,
  signal,
  ...(includePropertyDetail && {
    params: { include_property_detail: true, ...(download && { download }) },
  }),
});

export const reserveV2Excel = async (
  client: AxiosInstance,
  requestId: number,
  signal: AbortSignal,
  includePropertyDetail = false
): Promise<void> => {
  try {
    await client.post(
      v2DownloadUrl(requestId),
      {},
      v2ExcelConfig(signal, includePropertyDetail)
    );
  } catch (cause) {
    reportExcelError(cause, signal);
    throw cause;
  }
};

export const fetchV2Excel = async (
  client: AxiosInstance,
  requestId: number,
  signal: AbortSignal,
  includePropertyDetail = false,
  download = false
): Promise<V2ExcelExport> => {
  try {
    const response = await client.get<unknown>(
      v2DownloadUrl(requestId),
      v2ExcelConfig(signal, includePropertyDetail, download)
    );
    const parsed = v2ExcelSchema.safeParse(response.data);
    const signed = !includePropertyDetail || download;
    if (
      !parsed.success ||
      (parsed.data.status === "succeeded" &&
        signed &&
        (!parsed.data.url || !isHttpUrl(parsed.data.url)))
    ) {
      throw new V2ExcelContractError("Invalid V2 Excel response");
    }
    return parsed.data;
  } catch (cause) {
    reportExcelError(cause, signal);
    throw cause;
  }
};
